<template>
  <div class="home-cc">
    <!-- Hero -->
    <section class="home-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ eyebrow }}</div>
        <h1 class="home-hero__title">{{ greeting }}</h1>
        <i18n
            v-if="overdueCount || todayCount"
            :path="summaryPath"
            tag="p"
            class="home-hero__summary"
        >
          <b place="today">{{ $tc('home.summary.things', todayCount, { count: todayCount }) }}</b>
          <b place="overdue" class="home-hero__overdue">{{ $tc('home.summary.things', overdueCount, { count: overdueCount }) }}</b>
        </i18n>
        <p v-else class="home-hero__summary">{{ $t('home.summary.caught_up') }}</p>
        <p v-if="extraSummary" class="home-hero__summary home-hero__summary--extra">{{ extraSummary }}</p>

        <!-- Search: my tasks and projects -->
        <div
            class="home-search"
            @keydown.down.prevent="moveResult(1)"
            @keydown.up.prevent="moveResult(-1)"
            @keydown.enter.prevent="chooseResult()"
            @keydown.esc="closeSearch"
        >
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input
              ref="search"
              v-model="query"
              class="home-search__input"
              :placeholder="$t('home.search.placeholder')"
              qid="home-search"
              @focus="searchOpen = true"
              @blur="closeSearchSoon"
          />
          <span class="home-kbd">{{ isMac ? '⌘K' : 'Ctrl+K' }}</span>

          <div v-if="searchOpen && query.trim()" class="home-search__results">
            <template v-for="group in searchGroups">
              <div :key="'g-' + group.key" class="home-search__group">{{ $t('home.search.' + group.key) }}</div>
              <div
                  v-for="result in group.items"
                  :key="group.key + '-' + result.id"
                  class="home-search__result"
                  :class="{ 'is-active': flatResults.indexOf(result) === activeResult }"
                  @mousedown.prevent="chooseResult(result)"
              >
                <span class="home-search__name">{{ result.title }}</span>
                <span class="home-search__meta">{{ result.meta }}</span>
              </div>
            </template>
            <div v-if="!flatResults.length" class="home-search__empty">{{ $t('home.search.no_match') }}</div>
          </div>
        </div>

        <div class="home-hero__chips">
          <button
              v-if="canCreateProject"
              type="button"
              class="home-chip home-chip--light"
              qid="home-new-project"
              @click="$emit('create-project')"
          >
            <font-awesome-icon icon="plus"/>{{ $t('home.actions.new_project') }}
          </button>
          <button
              v-if="canCreatePartition"
              type="button"
              class="home-chip"
              qid="home-new-partition"
              @click="$emit('create-partition')"
          >
            <font-awesome-icon icon="layer-group"/>{{ $t('home.actions.new_partition') }}
          </button>
          <button
              v-if="canInviteUsers"
              type="button"
              class="home-chip"
              qid="home-invite-user"
              @click="$emit('invite-user')"
          >
            <font-awesome-icon icon="users"/>{{ $t('users_page.actions.invite') }}
          </button>
          <router-link
              v-if="canImportMeasures"
              class="home-chip"
              to="/maintenance/measures"
              qid="home-import-measures"
          >
            <font-awesome-icon icon="toolbox"/>{{ $t('maintenance.measures.import_button') }}
          </router-link>
          <router-link
              v-if="showProjects"
              class="home-chip"
              to="/projects"
              qid="home-gantt-link"
          >
            <font-awesome-icon icon="calendar-alt"/>{{ $t('projects.management.title') }}
          </router-link>
        </div>
      </div>

      <div class="home-week">
        <div class="home-week__top">
          <svg
              class="home-ring"
              width="96"
              height="96"
              viewBox="0 0 96 96"
          >
            <circle
                class="home-ring__track"
                cx="48"
                cy="48"
                r="42"
            />
            <circle
                v-if="week.done"
                class="home-ring__value"
                cx="48"
                cy="48"
                r="42"
                :stroke-dasharray="ringDash(week.done, week.total, 42)"
            />
          </svg>
          <b class="home-week__count">{{ week.done }}/{{ week.total }}</b>
          <div>
            <div class="home-week__title">{{ $t('home.week.title') }}</div>
            <div class="home-week__text">{{ weekText }}</div>
          </div>
        </div>
        <div class="home-week__stats">
          <div :class="{ 'is-red': overdueCount }"><b>{{ overdueCount }}</b>{{ $t('home.groups.overdue') }}</div>
          <div><b>{{ todayCount }}</b>{{ $t('home.groups.today') }}</div>
          <div><b>{{ stateCount('ready_to_test') }}</b>{{ $t('home.tags.ready_to_test') }}</div>
        </div>
      </div>
    </section>

    <!-- Focus now -->
    <div class="home-sec">
      <h2>{{ $t('home.focus.title') }}</h2>
      <span>{{ $t('home.focus.subtitle') }}</span>
    </div>
    <div class="home-focus">
      <div
          v-for="item in focus"
          :key="'focus-' + item.task.id"
          class="home-focus__card"
          :class="'is-' + item.kind"
          qid="home-focus-card"
      >
        <div class="home-focus__kind">
          <font-awesome-icon :icon="focusIcon(item.kind)"/>
          {{ focusLabel(item) }}
        </div>
        <div class="home-focus__title">{{ titleOf(item.task) }}</div>
        <div class="home-focus__text">{{ focusText(item) }}</div>
        <div class="home-focus__foot">
          <button type="button" class="home-btn home-btn--primary" @click="openRow(item.task)">
            <font-awesome-icon :icon="item.kind === 'sign_off' ? 'check' : 'up-right-from-square'"/>
            {{ item.kind === 'sign_off' ? $t('home.actions.sign_off') : $t('home.actions.open_task') }}
          </button>
        </div>
      </div>
      <div v-if="!loading && !focus.length" class="home-focus__card is-done">
        <div class="home-focus__kind"><font-awesome-icon icon="check-circle"/>{{ $t('home.focus.caught_up_kind') }}</div>
        <div class="home-focus__title">{{ $t('home.focus.caught_up') }}</div>
        <div class="home-focus__text">{{ $t('home.focus.caught_up_text') }}</div>
      </div>
      <div v-if="loading" class="home-focus__card is-loading"><div class="home-skeleton"></div><div class="home-skeleton home-skeleton--short"></div></div>
    </div>

    <div class="v2-grid home-body">
      <div class="v2-col">
        <!-- My work -->
        <section class="v2-card">
          <div class="v2-card__head home-work__head">
            <h3>{{ $t('home.work.title') }}</h3>
            <button v-if="projectGroups.length"
                    type="button"
                    class="home-btn home-btn--ghost home-btn--small"
                    qid="home-toggle-all"
                    @click="toggleAll"
            >
              <font-awesome-icon :icon="anyExpanded ? 'compress' : 'expand'"/>
              {{ anyExpanded ? $t('home.work.collapse_all') : $t('home.work.expand_all') }}
            </button>
          </div>
          <div class="home-work__tabs">
            <div class="v2-seg">
              <span
                  v-for="tab in tabs"
                  :key="tab"
                  :class="{ on: activeTab === tab }"
                  :qid="'home-tab-' + tab"
                  @click="activeTab = tab"
              >
                {{ $t('home.tabs.' + tab) }}
                <em class="home-count" :class="{ 'is-red': tab === 'rejected' && tabCount(tab) }">{{ tabCount(tab) }}</em>
              </span>
            </div>
          </div>

          <!-- Project, then control objective or element, then the tasks; the due date is the project's -->
          <div v-for="group in projectGroups" :key="group.key" class="home-pgroup" qid="home-project-group">
            <div class="home-group home-pgroup__head"
                 role="button"
                 tabindex="0"
                 :aria-expanded="isCollapsed(group.key) ? 'false' : 'true'"
                 @click="toggle(group.key)"
                 @keydown.enter="toggle(group.key)"
            >
              <font-awesome-icon :icon="isCollapsed(group.key) ? 'angle-right' : 'angle-down'"/>
              <span class="home-pgroup__title">{{ group.project ? group.project.title : $t('home.tags.rejected_validation') }}</span>
              <em class="home-count">{{ group.count }}</em>
              <span class="grow"></span>
              <span v-if="group.project" class="home-pgroup__due" :class="dueTone(group.due_date)">{{ dueText(group.due_date) }}</span>
            </div>
            <template v-if="!isCollapsed(group.key)">
              <home-task-row
                  v-for="task in group.ungrouped"
                  :key="'t-' + task.id"
                  :task="task"
                  @open="openRow"
              />
              <template v-for="sub in group.groups">
                <div :key="group.key + '-' + sub.key"
                     class="home-subgroup"
                     :class="{ 'is-collapsed': isCollapsed(subKey(group, sub)) }"
                     role="button"
                     tabindex="0"
                     :aria-expanded="isCollapsed(subKey(group, sub)) ? 'false' : 'true'"
                     qid="home-task-subgroup"
                     @click="toggle(subKey(group, sub))"
                     @keydown.enter="toggle(subKey(group, sub))"
                >
                  <font-awesome-icon :icon="isCollapsed(subKey(group, sub)) ? 'angle-right' : 'angle-down'" class="home-subgroup__chevron"/>
                  <span class="home-subgroup__icon" :class="'is-' + sub.kind"><font-awesome-icon :icon="sub.icon"/></span>
                  <span class="home-subgroup__title">{{ sub.title }}</span>
                  <em class="home-count">{{ sub.tasks.length }}</em>
                </div>
                <template v-if="!isCollapsed(subKey(group, sub))">
                  <home-task-row
                      v-for="task in sub.tasks"
                      :key="'t-' + task.id"
                      class="home-row--nested"
                      :task="task"
                      @open="openRow"
                  />
                </template>
              </template>
            </template>
          </div>

          <div v-if="loading" class="home-empty"><div class="home-skeleton"></div><div class="home-skeleton"></div><div class="home-skeleton home-skeleton--short"></div></div>
          <div v-else-if="!visibleTasks.length" class="home-empty">{{ $t('home.work.empty') }}</div>

          <div class="home-more">
            <span>{{ $tc('home.work.open_count', visibleTasks.length, { count: visibleTasks.length }) }}</span>
          </div>
        </section>

        <!-- My projects -->
        <section v-if="showProjects" class="v2-card">
          <div class="v2-card__head">
            <h3>{{ $t('tasks.my_projects') }}</h3>
            <span class="v2-card__sub">{{ $tc('home.projects.active', projects.length, { count: projects.length }) }}</span>
            <router-link to="/projects" class="v2-link">{{ $t('projects.management.title') }}</router-link>
          </div>
          <div class="home-projects">
            <div
                v-for="project in projects"
                :key="'pc-' + project.id"
                class="home-project"
                qid="home-project-card"
            >
              <div class="home-project__top">
                <span class="home-avatar" :class="'is-' + avatarColor(project.id)">{{ initials(project.title) }}</span>
                <div class="grow">
                  <router-link :to="{ name: 'projects.view', params: { slug: project.slug } }" class="home-project__name">{{ project.title }}</router-link>
                  <div class="home-project__type">{{ projectTypeLabel(project.type) }}</div>
                </div>
                <span class="home-pill" :class="'is-' + project.health">{{ $t('home.health.' + project.health) }}</span>
              </div>
              <div class="home-phases">
                <div
                    v-for="(step, index) in project.phase.steps"
                    :key="step"
                    :class="phaseClass(project, index)"
                ></div>
              </div>
              <div class="home-phases__labels">
                <span v-for="step in project.phase.steps" :key="'l-' + step" :class="{ 'is-current': step === project.phase.current }">{{ phaseLabel(project, step) }}</span>
              </div>
              <div class="home-project__foot">
                <span v-if="project.next_deadline" class="home-project__date">
                  <font-awesome-icon icon="calendar-alt"/>
                  {{ $t('home.projects.deadline_' + project.next_deadline.kind, { date: formatDay(project.next_deadline.date) }) }}
                </span>
                <span class="grow"></span>
                <button
                    v-if="project.for_me"
                    type="button"
                    class="home-pill is-violet home-pill--button"
                    @click="$emit('open-project-tasks', project)"
                >
                  {{ $tc('home.projects.for_me', project.for_me, { count: project.for_me }) }}
                </button>
                <span class="home-stack">
                  <span
                      v-for="member in project.team"
                      :key="'m-' + project.id + '-' + member.id"
                      v-b-tooltip.hover
                      class="home-avatar home-avatar--xs home-avatar--round"
                      :class="'is-' + avatarColor(member.id)"
                      :title="member.name"
                  >{{ initials(member.name) }}</span>
                </span>
              </div>
            </div>
            <div v-if="!loading && !projects.length" class="home-empty">{{ $t('home.projects.empty') }}</div>
          </div>
        </section>
      </div>

      <!-- Right column -->
      <div class="v2-col">
        <section class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('home.calendar.title') }}</h3></div>
          <div class="home-cal">
            <div v-for="day in weekDays" :key="day.date" :class="{ 'is-today': day.isToday }">
              {{ day.name }}<b>{{ day.number }}</b>
              <span class="home-cal__dots"><i v-for="(dot, index) in day.dots" :key="index" :class="'is-' + dot"></i></span>
            </div>
          </div>
          <div v-for="deadline in deadlines" :key="deadline.key" class="home-deadline">
            <span class="home-deadline__date">{{ formatShort(deadline.date) }}</span>
            <span class="grow">{{ deadline.title }}</span>
            <span class="home-tag" :class="'is-' + deadline.color">{{ deadline.label }}</span>
          </div>
          <div v-if="!deadlines.length" class="home-empty home-empty--small">{{ $t('home.calendar.empty') }}</div>
        </section>

        <section v-if="waiting.length" class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('home.waiting.title') }}</h3><span class="v2-card__sub">{{ $t('home.waiting.subtitle') }}</span></div>
          <div v-for="row in waiting" :key="'w-' + row.project.id" class="home-waiting">
            <div class="home-waiting__top">
              <span class="home-avatar home-avatar--xs" :class="'is-' + avatarColor(row.project.id)">{{ initials(row.project.title) }}</span>
              <router-link :to="{ name: 'projects.view', params: { slug: row.project.slug } }" class="grow home-waiting__name">{{ row.project.title }}</router-link>
              <span class="home-waiting__count">{{ $t('home.waiting.count', { delivered: row.delivered, total: row.total }) }}</span>
            </div>
            <div class="home-bar"><i :class="{ 'is-amber': waitingAtRisk(row) }" :style="{ width: Math.round(row.delivered * 100 / row.total) + '%' }"></i></div>
            <div class="home-waiting__hint">
              <span class="grow">{{ waitingHint(row) }}</span>
              <a
                  href="#"
                  class="v2-link"
                  qid="home-remind"
                  @click.prevent="$emit('remind', row.project)">{{ $t('home.waiting.remind') }}</a
              >
            </div>
          </div>
        </section>

        <section class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('home.activity.title') }}</h3></div>
          <div class="v2-card__body">
            <ActivityFeed compact :prepared="activityEntries" :empty-text="$t('home.activity.empty')"/>
          </div>
        </section>

        <HomeGuides :guides="home.guides"/>
      </div>
    </div>
  </div>
</template>

<script>
import HomeTaskRow from '@/views/Home/HomeTaskRow.vue';
import HomeGuides from '@/views/Home/HomeGuides.vue';
import ActivityFeed from '@/components/activity/feed/ActivityFeed.vue';
import { homeEntry } from '@/components/activity/feed/summaryEntries.js';
import { TABS, projectTree, focusTasks, daysUntil, dueGroup, dueTone, dueText, taskTitle } from '@/views/Home/homeTasks.js';

const moment = require('moment');

export default {
  name: 'HomeCommandCentre',
  components: { HomeTaskRow, HomeGuides, ActivityFeed },
  props: {
    home: { type: Object, required: true },
    validations: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    canCreateProject: { type: Boolean, default: false },
    canCreatePartition: { type: Boolean, default: false },
    canInviteUsers: { type: Boolean, default: false },
    canImportMeasures: { type: Boolean, default: false },
    showProjects: { type: Boolean, default: true }
  },
  data() {
    return {
      tabs: Object.keys(TABS),
      activeTab: 'all',
      // Collapsed projects and control objectives or elements
      collapsed: [],
      query: '',
      searchOpen: false,
      activeResult: 0,
      isMac: typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    };
  },
  computed: {
    getClient() {
      return this.$store.state.client;
    },
    user() {
      return this.$store.state.user || {};
    },
    today() {
      return this.home.today || moment().format('YYYY-MM-DD');
    },
    tasks() {
      return (this.home.tasks || []).concat(this.validations);
    },
    projects() {
      return this.home.projects || [];
    },
    waiting() {
      return this.home.waiting_on_clients || [];
    },
    activity() {
      return this.home.activity || [];
    },
    activityEntries() {
      return this.activity.map(row => homeEntry(this, row));
    },
    week() {
      return this.home.week || { done: 0, total: 0 };
    },
    visibleTasks() {
      const states = TABS[this.activeTab];
      return states ? this.tasks.filter(task => states.includes(task.state)) : this.tasks;
    },
    projectGroups() {
      return projectTree(this.visibleTasks);
    },
    groupKeys() {
      return [].concat(...this.projectGroups.map(group => [group.key].concat(group.groups.map(sub => this.subKey(group, sub)))));
    },
    anyExpanded() {
      return this.groupKeys.some(key => !this.collapsed.includes(key));
    },
    focus() {
      return focusTasks(this.tasks, this.today);
    },
    overdueCount() {
      return this.tasks.filter(task => dueGroup(task, this.today) === 'overdue').length;
    },
    todayCount() {
      return this.tasks.filter(task => dueGroup(task, this.today) === 'today').length;
    },
    summaryPath() {
      if (this.todayCount && this.overdueCount) {
        return 'home.summary.today_and_overdue';
      }
      return this.todayCount ? 'home.summary.today_only' : 'home.summary.overdue_only';
    },
    extraSummary() {
      const parts = [];
      const delivered = this.stateCount('ready_to_test');
      const signOffs = this.stateCount('sign_off');
      if (delivered) {
        parts.push(this.$tc('home.summary.ready', delivered, { count: delivered }));
      }
      if (signOffs) {
        parts.push(this.$tc('home.summary.sign_offs', signOffs, { count: signOffs }));
      }
      return parts.join(' ');
    },
    eyebrow() {
      const name = this.getClient ? this.getClient.name : '';
      return [name, moment().format('dddd D MMMM')].filter(Boolean).join(' · ');
    },
    greeting() {
      const hour = moment().hour();
      const key = hour < 12 ? 'morning' : (hour < 18 ? 'afternoon' : 'evening');
      const firstName = (this.user.name || '').split(' ')[0];
      return this.$t('home.greeting.' + key, { name: firstName });
    },
    weekText() {
      if (!this.week.total) {
        return this.$t('home.week.nothing');
      }
      const left = this.week.total - this.week.done;
      return left ? this.$t('home.week.left', { done: this.week.done, total: this.week.total, left }) : this.$t('home.week.all_done');
    },
    weekDays() {
      const start = moment(this.today, 'YYYY-MM-DD');
      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = start.clone().add(i, 'days');
        const date = day.format('YYYY-MM-DD');
        days.push({
          date,
          name: day.format('ddd'),
          number: day.format('D'),
          isToday: i === 0,
          dots: this.deadlinesOn(date).slice(0, 3).map(deadline => deadline.color)
        });
      }
      return days;
    },
    allDeadlines() {
      const list = [];
      const seen = {};
      this.projects.forEach(project => {
        if (project.next_deadline) {
          const key = project.id + '-' + project.next_deadline.kind;
          seen[key] = true;
          list.push({
            key,
            date: project.next_deadline.date,
            title: project.title,
            label: this.$t('home.calendar.' + project.next_deadline.kind),
            color: project.next_deadline.kind === 'collection' ? 'blue' : 'amber'
          });
        }
      });
      this.waiting.forEach(row => {
        const key = row.project.id + '-collection';
        if (row.due_date && !seen[key]) {
          list.push({ key, date: row.due_date, title: row.project.title, label: this.$t('home.calendar.collection'), color: 'blue' });
        }
      });
      return list.sort((a, b) => a.date.localeCompare(b.date));
    },
    deadlines() {
      return this.allDeadlines.filter(deadline => {
        const days = daysUntil(deadline.date, this.today);
        return days >= 0 && days < 7;
      }).slice(0, 4);
    },
    searchGroups() {
      const query = this.query.trim().toLowerCase();
      if (!query) {
        return [];
      }
      const tasks = this.tasks
        .filter(task => this.titleOf(task).toLowerCase().includes(query) || (task.project && task.project.title.toLowerCase().includes(query)) || (task.asset && task.asset.toLowerCase().includes(query)))
        .slice(0, 6)
        .map(task => ({ id: task.id, type: 'task', task, title: this.titleOf(task), meta: task.project ? task.project.title : this.$t('home.tags.rejected_validation') }));
      const projects = this.projects
        .filter(project => project.title.toLowerCase().includes(query))
        .slice(0, 4)
        .map(project => ({ id: project.id, type: 'project', project, title: project.title, meta: this.projectTypeLabel(project.type) }));
      return [{ key: 'tasks', items: tasks }, { key: 'projects', items: projects }].filter(group => group.items.length);
    },
    flatResults() {
      return [].concat(...this.searchGroups.map(group => group.items));
    }
  },
  watch: {
    query() {
      this.activeResult = 0;
    }
  },
  methods: {
    titleOf(task) {
      return taskTitle(this, task);
    },
    stateCount(state) {
      return this.tasks.filter(task => task.state === state).length;
    },
    tabCount(tab) {
      const states = TABS[tab];
      return states ? this.tasks.filter(task => states.includes(task.state)).length : this.tasks.length;
    },
    subKey(group, sub) {
      return group.key + '/' + sub.key;
    },
    isCollapsed(key) {
      return this.collapsed.includes(key);
    },
    toggle(key) {
      this.collapsed = this.isCollapsed(key)
        ? this.collapsed.filter(item => item !== key)
        : this.collapsed.concat([key]);
    },
    toggleAll() {
      this.collapsed = this.anyExpanded ? this.groupKeys.slice() : [];
    },
    dueTone(date) {
      return dueTone(date, this.today);
    },
    dueText(date) {
      return dueText(this, date, this.today);
    },
    openRow(task) {
      if (task.kind === 'validation') {
        this.$emit('open-validation', task);
      } else {
        this.$emit('open-task', task);
      }
    },
    focusIcon(kind) {
      return { overdue: 'clock', rejected: 'times-circle', ready_to_test: 'check', sign_off: 'signature', today: 'calendar-alt' }[kind];
    },
    focusLabel(item) {
      if (item.kind === 'overdue') {
        const days = -daysUntil(item.task.due_date, this.today);
        return this.$tc('home.focus.overdue', days, { count: days });
      }
      return this.$t('home.focus.' + item.kind);
    },
    focusText(item) {
      const task = item.task;
      const project = task.project ? task.project.title : '';
      if (item.kind === 'ready_to_test') {
        const parts = [project, this.$t('home.focus.delivered_text')];
        if (task.files) {
          parts.push(this.$tc('home.signals.files', task.files, { count: task.files }));
        }
        if (task.new_messages) {
          parts.push(this.$tc('home.signals.new_messages', task.new_messages, { count: task.new_messages }));
        }
        return parts.join(' · ');
      }
      if (item.kind === 'sign_off') {
        return project + ' · ' + this.$t('home.focus.sign_off_text');
      }
      if (task.kind === 'validation') {
        return this.$t('home.focus.validation_text');
      }
      return [project, task.asset, this.$t('home.states.' + task.state)].filter(Boolean).join(' · ');
    },
    ringDash(done, total, radius) {
      const length = 2 * Math.PI * radius;
      const share = total ? done / total : 0;
      return (length * share) + ' ' + length;
    },
    phaseClass(project, index) {
      const current = project.phase.steps.indexOf(project.phase.current);
      if (index < current) {
        return 'is-done';
      }
      return index === current ? 'is-current' : '';
    },
    phaseLabel(project, step) {
      return project.is_engagement ? this.$t('projects.engagement.phase_short.' + step) : this.$t('home.phases.' + step);
    },
    projectTypeLabel(type) {
      const labels = {
        update: 'projects.view.update_label',
        validation: 'projects.view.validation_label',
        control: 'projects.view.control_label',
        security_measure: 'projects.view.security_measure_label',
        risk_assessment: 'maintenance.assessments.type.risk_assessment',
        control_assessment: 'maintenance.assessments.type.control_assessment',
        bia_assessment: 'maintenance.assessments.type.bia_assessment'
      };
      return labels[type] ? this.$t(labels[type]) : '';
    },
    deadlinesOn(date) {
      return this.allDeadlines.filter(deadline => deadline.date === date);
    },
    waitingAtRisk(row) {
      const days = daysUntil(row.due_date, this.today);
      return row.overdue > 0 || (days !== null && days <= 7 && row.delivered / row.total < 0.5);
    },
    waitingHint(row) {
      const parts = [];
      if (row.overdue) {
        parts.push(this.$tc('home.waiting.overdue', row.overdue, { count: row.overdue }));
      } else if (row.due_date) {
        const days = daysUntil(row.due_date, this.today);
        parts.push(this.$tc('home.waiting.due_in', days, { count: days }));
      }
      if (row.returned) {
        parts.push(this.$tc('home.waiting.returned', row.returned, { count: row.returned }));
      }
      if (row.last_delivery_at) {
        parts.push(this.$t('home.waiting.last_delivery', { time: this.fromNow(row.last_delivery_at) }));
      }
      return parts.join(' · ');
    },
    initials(name) {
      return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
    },
    avatarColor(id) {
      return ['teal', 'blue', 'violet', 'amber', 'rose'][id % 5];
    },
    formatDay(date) {
      return moment(date, 'YYYY-MM-DD').format('D MMM');
    },
    formatShort(date) {
      return moment(date, 'YYYY-MM-DD').format('ddd D');
    },
    fromNow(date) {
      return moment(date).fromNow();
    },
    focusSearch() {
      this.$refs.search.focus();
    },
    closeSearch() {
      this.searchOpen = false;
      this.$refs.search.blur();
    },
    closeSearchSoon() {
      setTimeout(() => { this.searchOpen = false; }, 150);
    },
    moveResult(step) {
      const count = this.flatResults.length;
      if (count) {
        this.activeResult = (this.activeResult + step + count) % count;
      }
    },
    chooseResult(result) {
      const chosen = result || this.flatResults[this.activeResult];
      if (!chosen) {
        return;
      }
      this.searchOpen = false;
      this.query = '';
      if (chosen.type === 'task') {
        this.openRow(chosen.task);
      } else {
        this.$router.push({ name: 'projects.view', params: { slug: chosen.project.slug } });
      }
    },
    onKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        this.focusSearch();
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  }
};
</script>
